import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { CircleNotch, Plus, X } from "@phosphor-icons/react";
import useUnsavedGuard from "@/hooks/useUnsavedGuard";
import { formatPhone } from "@/lib/phone";

const EMPTY = {
  name: "", street_address: "", address_line2: "", city: "", state: "",
  zip_code: "", country: "USA", main_phone: "", market_segment_id: "", occupancy_classification_id: "",
  county: "", location_number: "", square_footage: "", service_status: "", notes: "",
};

function payload(form, siteContacts) {
  const out = {
    // Stable row keys are UI-only; strip them before sending.
    on_site_contacts: siteContacts
      .filter((s) => s.contact_id)
      .map((s) => ({ contact_id: s.contact_id, site_role: s.site_role || null })),
  };
  Object.entries(form).forEach(([k, v]) => { out[k] = v === "" ? null : v; });
  out.name = form.name;
  out.square_footage = form.square_footage === "" ? null : Number(form.square_footage);
  return out;
}

// Stable identifiers for repeated site-contact rows (never index-based).
let siteRowSeq = 0;
const newSiteRow = () => ({ contact_id: "", site_role: "", _key: `row-${++siteRowSeq}` });
const withRowKeys = (list) => (list || []).map((s) => ({ ...s, _key: `row-${++siteRowSeq}` }));

export default function PropertyDialog({ open, prospectId, property, segments, occupancies, serviceStatuses, contacts, onClose, onDone }) {
  // Legacy taxonomy ids resolve to their standardized replacement so editing a
  // pre-migration record shows (and saves) the approved value.
  const canonicalId = (id, options) => {
    if (!id) return "";
    const match = (options || []).find((o) => o.id === id || (o.legacy_ids || []).includes(id));
    return match ? match.id : id;
  };
  // State is initialized once per mount; the parent remounts this dialog via
  // a `key` whenever the target property changes, so no sync effect is needed.
  const [form, setForm] = useState(() => ({
    name: (property && property.name) || "",
    street_address: (property && property.street_address) || "",
    address_line2: (property && property.address_line2) || "",
    city: (property && property.city) || "",
    state: (property && property.state) || "",
    zip_code: (property && property.zip_code) || "",
    country: (property && property.country) || "USA",
    main_phone: (property && property.main_phone) || "",
    market_segment_id: canonicalId(property && property.market_segment_id, segments),
    occupancy_classification_id: canonicalId(property && property.occupancy_classification_id, occupancies),
    county: (property && property.county) || "",
    location_number: (property && property.location_number) || "",
    square_footage: (property && property.square_footage != null) ? String(property.square_footage) : "",
    service_status: (property && property.service_status) || "",
    notes: (property && property.notes) || "",
  }));
  const [siteContacts, setSiteContacts] = useState(() => withRowKeys(property && property.on_site_contacts));
  const [loading, setLoading] = useState(false);
  const guard = useUnsavedGuard([form, siteContacts]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (property) {
        await api.patch(`/properties/${property.id}`, payload(form, siteContacts));
        toast.success("Property updated");
      } else {
        await api.post(`/prospects/${prospectId}/properties`, payload(form, siteContacts));
        toast.success("Property added");
      }
      onDone();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const setSite = (rowKey, field, value) =>
    setSiteContacts((prev) => prev.map((s) => (s._key === rowKey ? { ...s, [field]: value } : s)));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent {...guard.guardProps} className="max-h-[90vh] overflow-y-auto rounded-sm sm:max-w-lg" data-testid="property-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#032852]">
            {property ? "Edit Property / Location" : "Add Property / Location"}
          </DialogTitle>
          <DialogDescription>A prospect may hold one location or an entire portfolio.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Property / location name *</Label>
            <Input required value={form.name} onChange={set("name")} className="rounded-sm" data-testid="property-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Street address</Label>
            <Input value={form.street_address} onChange={set("street_address")} className="rounded-sm" data-testid="property-street" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Address line 2</Label>
            <Input value={form.address_line2} onChange={set("address_line2")} className="rounded-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">City</Label>
              <Input value={form.city} onChange={set("city")} className="rounded-sm" data-testid="property-city" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">State</Label>
              <Input value={form.state} onChange={set("state")} className="rounded-sm" data-testid="property-state" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">ZIP</Label>
              <Input value={form.zip_code} onChange={set("zip_code")} className="rounded-sm" data-testid="property-zip" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Country</Label>
              <Input value={form.country} onChange={set("country")} className="rounded-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Main property phone</Label>
              <Input value={form.main_phone} onChange={set("main_phone")} onBlur={(e) => set("main_phone")({ target: { value: formatPhone(e.target.value) } })} className="rounded-sm" data-testid="property-phone" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Market segment</Label>
              <Select value={form.market_segment_id} onValueChange={(v) => setForm((f) => ({ ...f, market_segment_id: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="property-segment"><SelectValue placeholder="Select segment" /></SelectTrigger>
                <SelectContent>
                  {(segments || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Occupancy / ownership</Label>
              <Select value={form.occupancy_classification_id} onValueChange={(v) => setForm((f) => ({ ...f, occupancy_classification_id: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="property-occupancy"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  {(occupancies || []).map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">County</Label>
              <Input value={form.county} onChange={set("county")} className="rounded-sm" data-testid="property-county" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Location number</Label>
              <Input value={form.location_number} onChange={set("location_number")} className="rounded-sm" data-testid="property-location-number" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Square footage</Label>
              <Input type="number" min="0" step="any" value={form.square_footage} onChange={set("square_footage")} className="rounded-sm" data-testid="property-square-footage" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Service status</Label>
              <Select value={form.service_status} onValueChange={(v) => setForm((f) => ({ ...f, service_status: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="property-service-status"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  {(serviceStatuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={set("notes")} className="rounded-sm" data-testid="property-notes" />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-bold uppercase tracking-wide text-[#52525B]">On-site contacts</Label>
              <button
                type="button"
                className="flex items-center gap-1 text-xs font-semibold text-[#569A1F] hover:text-[#457d18]"
                onClick={() => setSiteContacts((prev) => [...prev, newSiteRow()])}
                data-testid="add-site-contact"
              >
                <Plus size={14} weight="bold" /> Add
              </button>
            </div>
            {siteContacts.length === 0 && (
              <p className="text-xs text-[#6D6D70]">Reference existing prospect contacts (e.g. Property Manager, Chief Engineer) — no duplicate entry needed.</p>
            )}
            {siteContacts.map((s, i) => (
              <div key={s._key} className="flex items-center gap-2" data-testid={`site-contact-row-${i}`}>
                <Select value={s.contact_id} onValueChange={(v) => setSite(s._key, "contact_id", v)}>
                  <SelectTrigger className="rounded-sm" data-testid={`site-contact-${i}`}><SelectValue placeholder="Contact" /></SelectTrigger>
                  <SelectContent>
                    {(contacts || []).map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={s.site_role || ""}
                  onChange={(e) => setSite(s._key, "site_role", e.target.value)}
                  placeholder="Site role"
                  className="rounded-sm"
                />
                <button type="button" className="p-1 text-[#6D6D70] hover:text-[#EF4444]"
                        onClick={() => setSiteContacts((prev) => prev.filter((row) => row._key !== s._key))}>
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]" data-testid="submit-property">
              {loading ? <CircleNotch size={18} className="animate-spin" /> : property ? "Save property" : "Add property"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
